import React, { FC, useState, useEffect } from 'react';
import {
  Divider,
  Card,
  CircularProgress,
  TableCell,
  TableRow,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Grid,
  TextField
} from '@mui/material';
import { API } from '../../../config/config';
import {
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
import { ZoomIn } from '@mui/icons-material';
import { descendingComparator } from '../../../Utils';
import { useAuth } from 'src/hooks/useAuth';
import PermissionsContent from '../Permission/PermissionsContent';
import CloseIcon from '@mui/icons-material/Close';
import LogoSpinner from 'src/components/LogoSpinner';
interface RecentOrdersTableProps {
  className?: string;
  retry: boolean;
}

const StaffAccountContent: FC<RecentOrdersTableProps> = (props) => {
  const { enhancedFetch } = useAuth();
  const [staffAccount, setStaffAccount] = useState<any[]>([]);
  const [searchData, setSearchData] = useState<any[] | null>(null);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [currentStaff, setCurrentStaff] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const { retry } = props;


  const fetchStaffAccounts = async () => {
    try {
      setLoading(true);
      const response = await enhancedFetch(
        `${API.HOST}/api/v1/staff-accounts`,
        {
          method: 'GET'
        }
      );
      const mapped = response?.map((user: any) => {
        const staffInfo = user?.userStaffInfo || {};
        return {
          id: staffInfo.id,
          firstName: staffInfo.firstName,
          lastName: staffInfo.lastName,
          phoneNumber: staffInfo.phoneNumber,
          email: staffInfo.email,
          permissions:
            user.permissions?.map((perm: any) => perm.permissionName) || []
        };
      });

      setStaffAccount(mapped);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch staff accounts:', error);
    }
  };

  useEffect(() => {
    fetchStaffAccounts();
  }, [retry]);

  useEffect(() => {
    const sorted = [...staffAccount].sort((a, b) => {
      if (sortAsc === 'asc') {
        return descendingComparator(a, b, sortBy);
      }
      return -descendingComparator(a, b, sortBy);
    });
    setStaffAccount(sorted);
  }, [sortBy, sortAsc]);

  const handleStaffSelect = (staff: any) => {
    setCurrentStaff(staff);
  };

  const handleSaveChanges = async () => {
    try {
      await enhancedFetch(
        `${API.HOST}/api/v1/staff-accounts/permissions?staffId=${currentStaff?.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(currentStaff?.permissions || [])
        }
      );

      // Refresh staff accounts to get updated data
      await fetchStaffAccounts();
      setCurrentStaff(null);
    } catch (error) {
      console.error('Failed to save permissions:', error);
    }
  };

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  return (
    <div>
      <Card>
        <Divider />
        <TableContainer>
          <DynamicTable
            sortByField={sortBy}
            onSort={(headerProperty) => {
              setSortBy(headerProperty);
              setSortAsc(sortAsc === 'asc' ? 'desc' : 'asc');
            }}
            sort={sortAsc}
            searchElement={
              <TableSearch
                placeholder={'Search Staffs'}
                searchData={staffAccount}
                setSearchData={setSearchData}
              />
            }

            rows={
              staffAccount?.map((staff) => ({
                id: staff.id,
                firstName: staff.firstName,
                lastName: staff.lastName,
                phoneNumber: staff.phoneNumber,
                email: staff.email,
                actions: staff,
                rowHeight: 60
              })) || []
            }

            headers={[

              { headerName: 'First Name', field: 'firstName' },
              { headerName: 'Last Name', field: 'lastName' },
              { headerName: 'Phone Number', field: 'phoneNumber' },
              { headerName: 'Email', field: 'email' },
              {
                headerName: 'Actions',
                field: 'actions',
                renderCell: (value) => (
                  <IconButton
                    onClick={() => handleStaffSelect(value.row.actions)}
                    color="inherit"
                    size="small"
                  >
                    <ZoomIn fontSize="small" />
                  </IconButton>
                )
              }
            ]}
          />
        </TableContainer>
      </Card>

      <Dialog
        open={currentStaff !== null}
        maxWidth="md"
        fullWidth
        onClose={() => setCurrentStaff(null)}
      >
        <DialogTitle>
          Staff Account Details
          <IconButton
            sx={{ position: 'absolute', right: 8, top: 8 }}
            onClick={() => setCurrentStaff(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {currentStaff && (
            <Stack spacing={3}>
              <Card sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mb: 1 }}
                >
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={currentStaff.firstName || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={currentStaff.lastName || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6} mt={'2%'}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={currentStaff.email || ''}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6} mt={"2%"}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={currentStaff.phoneNumber || ''}
                      disabled
                    />
                  </Grid>
                </Grid>
              </Card>

              <Card sx={{ p: 2 }}>
                <PermissionsContent
                  currentStaff={currentStaff}
                  setCurrentStaff={setCurrentStaff}
                />
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurrentStaff(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StaffAccountContent;
